import { useMemo, cloneElement, isValidElement } from 'react';
import { Layout, Menu } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Image } from 'semantic-ui-react';
import { useAuth } from '../contexts/auth';
import { useSidebar, useSidebarMetadata } from '../contexts';
import { applyInventorySidebarCounts } from '../utility/applyInventorySidebarCounts';
import { CommonUtility } from '../utility/common';
import { Images } from '../images';
import { formatBadge, getMenuSelection, sumChildCounts } from './sidebarMenuUtils';

const { Sider } = Layout;

const BADGE_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 16,
  minWidth: 16,
  padding: '0 4px',
  borderRadius: 3,
  background: '#FF4D4F',
  color: '#fff',
  fontSize: 10,
  fontWeight: 600,
  lineHeight: 1,
  flexShrink: 0,
  boxSizing: 'border-box',
};

function BadgePill({ count }) {
  const text = formatBadge(count);
  const singleDigit = text.length <= 1;
  return (
    <span style={{ ...BADGE_STYLE, padding: singleDigit ? 0 : '0 4px' }}>{text}</span>
  );
}

function GroupLabel({ label, badge }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'nowrap',
        paddingLeft: 12,
      }}
    >
      <span>{label.toUpperCase()}</span>
      {!!badge && <BadgePill count={badge} />}
    </span>
  );
}

function SubmenuItemLabel({ icon, label, badge }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'nowrap',
      }}
    >
      {isValidElement(icon) && (
        <span style={{ display: 'inline-flex', lineHeight: 0, color: 'inherit' }}>
          {cloneElement(icon, { size: 18, weight: 'regular' })}
        </span>
      )}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'nowrap',
        }}
      >
        <span>{label}</span>
        {!!badge && <BadgePill count={badge} />}
      </span>
    </span>
  );
}

const menuLinkStyle = {
  color: 'inherit',
  textDecoration: 'none',
  display: 'block',
};

function MenuNavLink({ to, children }) {
  if (!to) return children;
  return (
    <Link to={to} style={menuLinkStyle}>
      {children}
    </Link>
  );
}

function buildMenuItems(menus) {
  return menus.map(group => {
    const groupBadge = sumChildCounts(group.menus) || group.count;

    return {
      key: group.key,
      label: <GroupLabel label={group.text} badge={groupBadge || undefined} />,
      style: { paddingLeft: 0 },
      children: (group.menus || []).map(child => ({
        key: child.key,
        label: (
          <MenuNavLink to={child.link}>
            <SubmenuItemLabel
              icon={child.phosphorIcon}
              label={child.text}
              badge={child.count}
            />
          </MenuNavLink>
        ),
        style: { paddingLeft: 12 },
      })),
    };
  });
}

function SidebarLogo() {
  const { currentOrg } = useAuth();

  const isTactical = useMemo(
    () => currentOrg?.name?.toLowerCase?.()?.includes?.('tactical'),
    [currentOrg],
  );

  if (isTactical) {
    return (
      <div style={{ padding: '12px', display: 'flex', justifyContent: 'center' }}>
        <Image src={CommonUtility.getCdnLink('integrations/tactical.svg')} />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '8px 10px 8px 12px',
        flexShrink: 0,
      }}
    >
      <Link to="/app/fulfill/orders">
        <img src={Images.logo} alt="Skupreme" height={24} style={{ display: 'block' }} />
      </Link>
    </div>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const { menus } = useSidebar();
  const { inventory } = useSidebarMetadata();

  const menusWithBadges = useMemo(
    () => applyInventorySidebarCounts(menus, inventory),
    [menus, inventory],
  );

  const { selectedKey, openGroupKey } = useMemo(
    () => getMenuSelection(menusWithBadges, location.pathname),
    [menusWithBadges, location.pathname],
  );

  const menuItems = useMemo(() => buildMenuItems(menusWithBadges), [menusWithBadges]);

  return (
    <Sider
      width={240}
      theme="light"
      style={{
        borderRight: '1px solid #E8E8E8',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        alignSelf: 'stretch',
        height: '100%',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <SidebarLogo />
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <Menu
            mode="inline"
            inlineIndent={0}
            selectedKeys={selectedKey ? [selectedKey] : []}
            defaultOpenKeys={openGroupKey ? [openGroupKey] : []}
            items={menuItems}
            style={{ border: 'none' }}
          />
        </div>
      </div>
    </Sider>
  );
}
